import React, { useState } from 'react';
import { Card, CardContent } from '../components/Card';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Badge } from '../components/Badge';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/Sheet';
import { ScrollArea } from '../components/ScrollArea';
import { Separator } from '../components/Separator';
import { Search, Eye, Filter, ShieldAlert, CheckCircle2, Clock, FileText, MessageSquare, Bot, Check, Edit, ArrowRight, History } from 'lucide-react';
const mockCases = [{
  id: '1',
  patient: 'William George',
  initials: 'WG',
  amount: '£820.00',
  date: '12/03/2026',
  insurer: 'Bupa',
  status: 'Disputed',
  action: 'Agent drafted response'
}, {
  id: '2',
  patient: 'Joan Smith',
  initials: 'JS',
  amount: '£1,450.00',
  date: '11/03/2026',
  insurer: 'AXA Health',
  status: 'Accepted',
  action: 'Approved first pass'
}, {
  id: '3',
  patient: 'Mary Williams',
  initials: 'MW',
  amount: '£340.00',
  date: '10/03/2026',
  insurer: 'Aviva',
  status: 'Submitted',
  action: 'Awaiting insurer review'
}, {
  id: '4',
  patient: 'James Brown',
  initials: 'JB',
  amount: '£2,100.00',
  date: '09/03/2026',
  insurer: 'Vitality',
  status: 'Resolved',
  action: 'Query auto-resolved'
}, {
  id: '5',
  patient: 'Patricia Taylor',
  initials: 'PT',
  amount: '£560.00',
  date: '08/03/2026',
  insurer: 'Bupa',
  status: 'Accepted',
  action: 'Approved first pass'
}, {
  id: '6',
  patient: 'Robert Miller',
  initials: 'RM',
  amount: '£980.00',
  date: '07/03/2026',
  insurer: 'AXA Health',
  status: 'Disputed',
  action: 'Agent drafting response...'
}, {
  id: '7',
  patient: 'Linda Davis',
  initials: 'LD',
  amount: '£420.00',
  date: '06/03/2026',
  insurer: 'Aviva',
  status: 'Submitted',
  action: 'Awaiting insurer review'
}];
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'Accepted':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'Disputed':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'Submitted':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Resolved':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return '';
  }
};
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'Accepted':
      return <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-300" />;
    case 'Disputed':
      return <ShieldAlert className="w-4 h-4 text-amber-600" data-id="element-301" />;
    case 'Submitted':
      return <Clock className="w-4 h-4 text-blue-600" data-id="element-302" />;
    case 'Resolved':
      return <CheckCircle2 className="w-4 h-4 text-gray-600" data-id="element-303" />;
    default:
      return null;
  }
};
export function InsuranceCases() {
  const [selectedCase, setSelectedCase] = useState<(typeof mockCases)[0] | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const handleRowClick = (c: (typeof mockCases)[0]) => {
    setSelectedCase(c);
    setIsSheetOpen(true);
  };
  return <div className="p-8 max-w-[1600px] mx-auto space-y-8" data-id="element-304">
      <div data-id="element-305">
        <h1 className="font-heading text-4xl font-normal text-foreground mb-2" data-id="element-306">
          Insurance Cases
        </h1>
        <p className="text-muted-foreground text-lg" data-id="element-307">
          Track submissions, approvals, disputes and resolutions
        </p>
      </div>

      <div className="flex justify-between items-center gap-4" data-id="element-308">
        <div className="relative flex-1 max-w-md" data-id="element-309">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" data-id="element-310" />
          <Input placeholder="Search by patient name, ID, or insurer..." className="pl-9 bg-card" data-id="element-311" />
        </div>
        <Button variant="outline" className="gap-2 bg-card" data-id="element-312">
          <Filter className="w-4 h-4" data-id="element-313" />
          Filter Cases
        </Button>
      </div>

      <Card data-id="element-314">
        <Table data-id="element-315">
          <TableHeader data-id="element-316">
            <TableRow data-id="element-317">
              <TableHead data-id="element-318">Patient</TableHead>
              <TableHead data-id="element-319">Billing Amount</TableHead>
              <TableHead data-id="element-320">Submitted Date</TableHead>
              <TableHead data-id="element-321">Insurer</TableHead>
              <TableHead data-id="element-322">Status</TableHead>
              <TableHead data-id="element-323">AI Action</TableHead>
              <TableHead className="text-right" data-id="element-324">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-325">
            {mockCases.map(row => <TableRow key={row.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => handleRowClick(row)} data-id="element-326">
                <TableCell className="font-medium" data-id="element-327">
                  <div className="flex items-center gap-3" data-id="element-328">
                    <Avatar className="h-8 w-8 border border-border" data-id="element-329">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs" data-id="element-330">
                        {row.initials}
                      </AvatarFallback>
                    </Avatar>
                    {row.patient}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm" data-id="element-331">
                  {row.amount}
                </TableCell>
                <TableCell data-id="element-332">{row.date}</TableCell>
                <TableCell data-id="element-333">{row.insurer}</TableCell>
                <TableCell data-id="element-334">
                  <div className="flex items-center gap-2" data-id="element-335">
                    {getStatusIcon(row.status)}
                    <Badge variant="outline" className={getStatusBadgeClass(row.status)} data-id="element-336">
                      {row.status}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm flex items-center gap-2" data-id="element-337">
                  {row.status === 'Disputed' && <Bot className="w-4 h-4 text-primary" data-id="element-338" />}
                  {row.action}
                </TableCell>
                <TableCell className="text-right" data-id="element-339">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-id="element-340">
                    <Eye className="w-4 h-4" data-id="element-341" />
                  </Button>
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </Card>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen} data-id="element-342">
        <SheetContent side="right" className="w-[600px] sm:max-w-none p-0 flex flex-col bg-background" data-id="element-343">
          {selectedCase && <>
              {/* Header */}
              <div className="p-6 border-b border-border bg-card" data-id="element-344">
                <div className="flex justify-between items-start mb-4" data-id="element-345">
                  <div data-id="element-346">
                    <SheetTitle className="font-heading text-2xl mb-1" data-id="element-347">
                      Case Detail: {selectedCase.patient}
                    </SheetTitle>
                    <SheetDescription className="flex items-center gap-2" data-id="element-348">
                      <span data-id="element-349">Insurer: {selectedCase.insurer}</span>
                      <span data-id="element-350">•</span>
                      <span data-id="element-351">Submitted: {selectedCase.date}</span>
                    </SheetDescription>
                  </div>
                  <Badge variant="outline" className={`text-sm px-3 py-1 ${getStatusBadgeClass(selectedCase.status)}`} data-id="element-352">
                    {selectedCase.status}
                  </Badge>
                </div>
              </div>

              <ScrollArea className="flex-1 p-6" data-id="element-353">
                <div className="space-y-8" data-id="element-354">
                  {/* Bill Summary */}
                  <div className="space-y-3" data-id="element-355">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-356">
                      <FileText className="w-4 h-4 text-muted-foreground" data-id="element-357" />
                      Original Bill Summary
                    </h3>
                    <Card className="bg-secondary/30" data-id="element-358">
                      <CardContent className="p-4" data-id="element-359">
                        <div className="flex justify-between items-center mb-2 text-sm" data-id="element-360">
                          <span className="text-muted-foreground" data-id="element-361">
                            Total Amount Billed:
                          </span>
                          <span className="font-mono font-bold text-foreground" data-id="element-362">
                            {selectedCase.amount}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-363">
                          <span className="text-muted-foreground" data-id="element-364">
                            Primary Diagnosis Code:
                          </span>
                          <Badge variant="outline" className="font-mono bg-card" data-id="element-365">
                            G46.0
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {selectedCase.status === 'Disputed' && <>
                      {/* Insurer Dispute */}
                      <div className="space-y-3" data-id="element-366">
                        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-367">
                          <ShieldAlert className="w-4 h-4 text-amber-600" data-id="element-368" />
                          Insurer Dispute Details
                        </h3>
                        <Card className="border-amber-200 bg-amber-50/50" data-id="element-369">
                          <CardContent className="p-4" data-id="element-370">
                            <p className="text-sm text-amber-900 italic border-l-2 border-amber-400 pl-3 py-1" data-id="element-371">
                              "Claim denied. Code G46.0 (Middle cerebral artery
                              syndrome) requires documented evidence of acute
                              neurological deficit and corresponding imaging
                              findings within the encounter notes. Provided
                              documentation is insufficient to support this
                              level of coding."
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* AI Agent Response */}
                      <div className="space-y-3" data-id="element-372">
                        <div className="flex justify-between items-center" data-id="element-373">
                          <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-374">
                            <Bot className="w-4 h-4 text-primary" data-id="element-375" />
                            AI Agent Response Draft
                          </h3>
                          <Badge variant="secondary" className="bg-primary/10 text-primary" data-id="element-376">
                            Ready for Review
                          </Badge>
                        </div>
                        <Card className="border-primary/20 shadow-sm" data-id="element-377">
                          <CardContent className="p-5 space-y-4" data-id="element-378">
                            <p className="text-sm text-foreground leading-relaxed" data-id="element-379">
                              Dear {selectedCase.insurer} Claims Team,
                            </p>
                            <p className="text-sm text-foreground leading-relaxed" data-id="element-380">
                              We are appealing the denial for claim regarding
                              patient {selectedCase.patient}. The coding of
                              G46.0 is fully supported by the clinical
                              documentation in the attached encounter notes.
                            </p>

                            <div className="bg-secondary/50 p-3 rounded-md border border-border space-y-2" data-id="element-381">
                              <p className="text-xs font-bold text-muted-foreground uppercase" data-id="element-382">
                                Clinical Evidence Citations:
                              </p>
                              <ul className="list-disc pl-5 text-sm text-foreground space-y-2" data-id="element-383">
                                <li data-id="element-384">
                                  <strong data-id="element-385">Neurological Deficit:</strong>{' '}
                                  "Patient presented with acute onset left-sided
                                  hemiparesis and facial droop..." (Discharge
                                  Summary, Page 1, HPI)
                                </li>
                                <li data-id="element-386">
                                  <strong data-id="element-387">Imaging Findings:</strong> "MRI Brain
                                  without contrast reveals acute restricted
                                  diffusion in the right middle cerebral artery
                                  territory..." (Radiology Report, 11/03/2026)
                                </li>
                              </ul>
                            </div>

                            <p className="text-sm text-foreground leading-relaxed" data-id="element-388">
                              Based on these documented findings, G46.0 is the
                              most accurate and appropriate code for this
                              encounter. Please review the highlighted sections
                              in the attached documentation and process the
                              claim for payment.
                            </p>

                            <div className="flex gap-3 pt-4 border-t border-border" data-id="element-389">
                              <Button className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-id="element-390">
                                <Check className="w-4 h-4" data-id="element-391" />
                                Approve & Send Response
                              </Button>
                              <Button variant="outline" className="flex-1 gap-2" data-id="element-392">
                                <Edit className="w-4 h-4" data-id="element-393" />
                                Edit Response
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </>}

                  {/* Audit Trail */}
                  <div className="space-y-3" data-id="element-394">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-395">
                      <History className="w-4 h-4 text-muted-foreground" data-id="element-396" />
                      Activity Timeline
                    </h3>
                    <div className="pl-2 space-y-4 border-l-2 border-border ml-2" data-id="element-397">
                      {selectedCase.status === 'Disputed' && <div className="relative pl-6" data-id="element-398">
                          <div className="absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center border-4 border-background" data-id="element-399">
                            <Bot className="w-2 h-2 text-primary-foreground" data-id="element-400" />
                          </div>
                          <p className="text-sm font-medium text-foreground" data-id="element-401">
                            AI Agent drafted appeal response
                          </p>
                          <p className="text-xs text-muted-foreground" data-id="element-402">
                            Today at 10:45 AM
                          </p>
                        </div>}
                      {(selectedCase.status === 'Disputed' || selectedCase.status === 'Resolved') && <div className="relative pl-6" data-id="element-403">
                          <div className="absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center border-4 border-background" data-id="element-404">
                            <ShieldAlert className="w-2 h-2 text-white" data-id="element-405" />
                          </div>
                          <p className="text-sm font-medium text-foreground" data-id="element-406">
                            Insurer disputed claim
                          </p>
                          <p className="text-xs text-muted-foreground" data-id="element-407">
                            Today at 09:12 AM
                          </p>
                        </div>}
                      <div className="relative pl-6" data-id="element-408">
                        <div className="absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center border-4 border-background" data-id="element-409">
                          <Check className="w-2 h-2 text-white" data-id="element-410" />
                        </div>
                        <p className="text-sm font-medium text-foreground" data-id="element-411">
                          Claim submitted to {selectedCase.insurer}
                        </p>
                        <p className="text-xs text-muted-foreground" data-id="element-412">
                          {selectedCase.date} at 14:30 PM
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </>}
        </SheetContent>
      </Sheet>
    </div>;
}